/**
 * Lebar dan kedudukan kolom sumur pada lembar korelasi.
 *
 * Sampai sekarang kolom-kolom lembar ini bersentuhan: yang memisahkan dua
 * sumur hanyalah garis setebal satu piksel, dan sebuah kurva yang menyentuh
 * tepi kanan tracknya bersambung langsung dengan kurva sumur berikutnya.
 * Lembar korelasi yang dicetak orang tidak begitu — antar sumur ada ruang
 * kosong, dan di ruang itulah garis ikat terlihat menyeberang alih-alih
 * sekadar berbelok.
 *
 * Spasinya dinyatakan sebagai **pecahan lebar satu kolom**, bukan piksel, dan
 * itu bukan kerapian melainkan syarat. Garis ikat lembar ini digambar di ruang
 * abstrak 1000 × 1000 yang direntangkan dengan `preserveAspectRatio="none"`,
 * justru supaya tidak ada satu pun ukuran piksel yang harus diukur ulang
 * setiap kali jendela berubah. Spasi berupa piksel akan merusak sifat itu:
 * lebar kolom menjadi (lebar panel − n·gap)/n, yang tidak bisa dinyatakan di
 * ruang abstrak tanpa mengukur panel lebih dulu — dan yang diukur akan basi
 * satu bingkai setiap kali rel kanan dilipat. Sebagai pecahan, seluruh
 * hitungannya tetap murni: kolom dan spasi sama-sama tumbuh, dan garis ikatnya
 * tetap menempel pada kolomnya lewat resize apa pun tanpa satu pun pengamat.
 */

/** Pilihan lebar spasi di rel, sebagai pecahan lebar satu kolom. */
export const CORR_GAPS: { value: number; label: string }[] = [
  { value: 0, label: "None" },
  { value: 0.08, label: "Narrow" },
  { value: 0.18, label: "Medium" },
  { value: 0.32, label: "Wide" },
];

/** Spasi bawaan: cukup untuk memisahkan, belum cukup untuk memboroskan lebar. */
export const CORR_GAP_DEFAULT = 0.18;

/**
 * Spasi yang sah: bukan angka negatif, dan tidak melebihi satu lebar kolom.
 *
 * Batas atasnya bukan selera. Pada spasi selebar dua kolom, enam sumur
 * menghabiskan dua pertiga lembar untuk ruang kosong, dan yang tersisa untuk
 * kurvanya lebih sempit daripada kepala tracknya sendiri.
 */
export function clampGap(gap: number): number {
  if (!Number.isFinite(gap) || gap <= 0) return 0;
  return Math.min(1, gap);
}

/**
 * Tepi kiri dan kanan tiap kolom, sebagai pecahan lebar lembar.
 *
 * Satu kolom tidak punya spasi untuk ditempati, jadi ia selalu memenuhi
 * lembarnya — memberi spasi pada sumur tunggal berarti menyempitkan
 * satu-satunya yang ada demi ruang kosong yang tidak memisahkan apa pun.
 */
export function columnBounds(count: number, gap: number): [number, number][] {
  if (!Number.isInteger(count) || count <= 0) return [];
  if (count === 1) return [[0, 1]];
  const g = clampGap(gap);
  // Lebar total dalam satuan "satu kolom": n kolom dan n−1 spasi di antaranya.
  const total = count + (count - 1) * g;
  const step = (1 + g) / total;
  const width = 1 / total;
  return Array.from({ length: count }, (_, index) => {
    const start = index * step;
    return [start, start + width];
  });
}

/**
 * Titik tengah tiap kolom, sebagai pecahan lebar lembar.
 *
 * Inilah yang dipakai garis ikat. Dulu ia dihitung di tempat sebagai
 * `(kolom + 0,5) / jumlah`, yang benar tepat selama tidak ada spasi — dan
 * diam-diam meleset ke arah tepi begitu ada. Melesetnya paling besar di kolom
 * terluar, jadi yang pertama terlihat salah adalah garis ikat sumur pertama
 * dan terakhir: keduanya menggantung di sebelah tracknya, bukan di atasnya.
 */
export function columnCentres(count: number, gap: number): number[] {
  return columnBounds(count, gap).map(([start, end]) => (start + end) / 2);
}

/**
 * Titik-titik garis ikat sebuah marker: mendatar selebar tiap kolom yang
 * memuatnya, lalu miring menyeberangi spasi ke kolom berikutnya.
 *
 * Begitulah lembar korelasi cetak menariknya. Garis yang hanya menyambung
 * titik tengah kolom memotong lognya secara miring, sehingga kedalaman top di
 * dalam sebuah sumur tidak bisa dibaca dari garisnya — ia hanya tepat di satu
 * titik, di tengah kolom, dan meleset di kedua tepinya. Mendatar di dalam
 * kolom, garisnya jatuh tepat pada kedalaman top di seluruh lebar ketiga
 * tracknya, dan kemiringannya berpindah ke spasi, tempat ia memang berarti:
 * seberapa jauh top itu naik atau turun dari sumur ke sumur.
 *
 * `stops` sudah terurut kiri ke kanan; kolom yang tidak memuat marker itu
 * tidak ikut, jadi garisnya menyeberangi kolom itu langsung ke kolom
 * berikutnya yang memuatnya.
 */
export function tieLinePoints(
  stops: { start: number; end: number; y: number }[],
): [number, number][] {
  return stops.flatMap(({ start, end, y }): [number, number][] => [
    [start, y],
    [end, y],
  ]);
}

/**
 * Bobot `flex` untuk satu kolom dan satu spasi.
 *
 * Dipakai apa adanya sebagai `flex` di CSS: sebuah kolom berbobot 1 dan sebuah
 * spasi berbobot `gap`, sehingga tata letak yang digambar browser adalah tata
 * letak yang sama dengan yang dihitung `columnBounds` di atas. Keduanya harus
 * berasal dari satu angka; dua tempat yang masing-masing memutuskan sendiri
 * seberapa lebar spasinya adalah garis ikat yang meleset tanpa ada yang tahu
 * sebabnya.
 */
export function gapFlex(gap: number): number {
  return clampGap(gap);
}
